import { CSSObject } from "@emotion/react";
import { constants, typographyStyles } from "@newsela/angelou";

import {
  gradientBackgrounds,
  partialGradientColors,
} from "@/components/AIComponents/AiComponents.style";
import {
  checksForUnderstandingEyebrowFontSizes,
  checksForUnderstandingFontSizes,
  checksForUnderstandingToggleFontSizes,
} from "@/view/constants/styles/accessibleFontSizeMapping/accessibleFontSizeMapping.style";
import { QuizSubmissionStatus } from "./ChecksForUnderstandingHeader";

const { fontFamily } = typographyStyles;
const { ui } = constants.colors;

const headerBgColors = {
  locked: ui.beige[50],
  correct: ui.green[700],
  incorrect: ui.neutral[600],
};

export const iconCss = (status: QuizSubmissionStatus) =>
  status === "locked"
    ? {
        flexShrink: 0,
      }
    : {
        flexShrink: 0,
        circle: { fill: "white", stroke: "none" },
        path: { stroke: headerBgColors[status] },
      };

const getHeaderColor = (
  status: QuizSubmissionStatus,
  showLunaAiBranding: boolean,
) => {
  let color: string = ui.white[100];
  if (showLunaAiBranding) {
    if (status === "locked") {
      color = ui.neutral[700];
    } else if (status === "incorrect") {
      color = ui.neutral[500];
    }
  }
  return color;
};

const getHeaderBackground = (
  status: QuizSubmissionStatus,
  showLunaAiBranding: boolean,
) => {
  let headerBackground: string = headerBgColors[status];
  if (showLunaAiBranding) {
    if (status === "locked") {
      headerBackground = gradientBackgrounds[100];
    } else if (status === "correct") {
      headerBackground = `linear-gradient(
          279.44deg,
          ${partialGradientColors.position100.color} 4.6%,
          ${partialGradientColors.position25.color} 66.61%,
          ${partialGradientColors.position0.color} 100%
        )`;
    }
  }
  return headerBackground;
};

export const headerCss = (
  status: QuizSubmissionStatus,
  showLunaAiBranding: boolean,
): CSSObject => {
  const headerColor = getHeaderColor(status, showLunaAiBranding);
  const headerBackground = getHeaderBackground(status, showLunaAiBranding);

  return {
    padding: "1em",
    color: headerColor,
    background: headerBackground,
    marginBottom: 0,
    display: "block",
    position: "relative",
    width: "100%",
    textAlign: "left",
    lineHeight: 0,
    borderRadius: "7px 7px 0 0",
    userSelect: "none",

    "& > div:first-of-type": {
      width: "100%",
    },
  };
};

export const aiTopicsNoticeWrapperCss: CSSObject = {
  position: "absolute",
  top: "1em",
  right: "1em",
};

export const eyebrowCss = (
  currentFontSize: string = checksForUnderstandingEyebrowFontSizes.small,
  status: QuizSubmissionStatus,
  showLunaAiBranding?: boolean,
): CSSObject => ({
  color:
    status === "locked"
      ? showLunaAiBranding
        ? ui.neutral[700]
        : ui.neutral[500]
      : ui.white[100],
  textTransform: "uppercase",
  fontFamily: "Circular",
  fontWeight: "normal",
  lineHeight: "normal",
  marginBottom: "8px",
  letterSpacing: "1px",
  width: "calc(100% - 75px)",
  fontSize:
    checksForUnderstandingEyebrowFontSizes[
      currentFontSize as keyof typeof checksForUnderstandingEyebrowFontSizes
    ],
});

export const lockMessageCss = (status: QuizSubmissionStatus) => ({
  color: status === "locked" ? ui.neutral[700] : ui.white[100],
  fontWeight: "bold",
  display: "flex",
  gap: ".5em",
  lineHeight: 1.5,
  ...fontFamily.noto,
});

export const answersCss = {
  paddingInlineStart: "0",
  li: {
    display: "flex",
    paddingBottom: ".5em",
    marginInlineStart: "0",
  },
};

export const textContainerCss = (
  currentFontSize: string = checksForUnderstandingFontSizes.small,
) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  width: "100%",
  fontSize:
    checksForUnderstandingFontSizes[
      currentFontSize as keyof typeof checksForUnderstandingEyebrowFontSizes
    ],
});

export const collapseButtonTriggerCss = (
  currentFontSize: string = checksForUnderstandingToggleFontSizes.small,
) => ({
  ...fontFamily.circular,
  fontSize:
    checksForUnderstandingToggleFontSizes[
      currentFontSize as keyof typeof checksForUnderstandingToggleFontSizes
    ],
  fontWeight: "700",
  textDecoration: "underline",
});
